import { useCallback, useEffect } from "react"

import { logout } from "@/actions/auth"
import { useAppDispatch, useAppSelector, useAppStore } from "@/lib/hooks"
import { useInterval } from "./use_interval"

const DEFAULT_SESSION_TTL_AFTER_LOGIN_MS = 3600 * 1000
const DEFAULT_SESSION_TTL_AFTER_LAST_HEARTBEAT_MS = 120 * 1000
const HEARTBEAT_INTERVAL_MS = 500
const EXPIRY_CHECK_INTERVAL_MS = 1000

const readDevOptionSeconds = (key: keyof NonNullable<Window["_phenoDevOptions"]>): number | null => {
  const value = window._phenoDevOptions?.[key]
  return typeof value === "number" ? value : null
}

// In private-browsing modes localStorage throws on write. Patch it to a
// no-op (after stashing the original on a `_setItem` escape hatch) so
// redux-persist's storage layer keeps working.
const verifyLocalStorage = (): void => {
  try {
    localStorage.setItem("private-browser-detection", "1")
    localStorage.removeItem("private-browser-detection")
  } catch {
    const proto = Storage.prototype as Storage & {
      _setItem?: typeof Storage.prototype.setItem
    }
    proto._setItem = Storage.prototype.setItem
    Storage.prototype.setItem = function () {}
  }
}

const useSessionExpiryGuard = (): void => {
  const dispatch = useAppDispatch()
  const auth = useAppSelector((s) => s.auth)
  const store = useAppStore()
  const updateSessionHeartbeat = useCallback((): void => {
    dispatch({ type: "sessionHeartbeatAt", payload: Date.now() })
  }, [dispatch])
  const checkSessionAlive = useCallback((): void => {
    const state = store.getState()
    if (!state.auth) return
    const loginTtlOverride = readDevOptionSeconds("sessionTtlAfterLogin")
    const heartbeatTtlOverride = readDevOptionSeconds("sessionTtlAfterLastHeartBeat")
    const loginTtlMs = loginTtlOverride !== null ? loginTtlOverride * 1000 : DEFAULT_SESSION_TTL_AFTER_LOGIN_MS
    const heartbeatTtlMs =
      heartbeatTtlOverride !== null ? heartbeatTtlOverride * 1000 : DEFAULT_SESSION_TTL_AFTER_LAST_HEARTBEAT_MS
    const now = Date.now()
    const heartbeatNotYetEmitted = state.sessionHeartbeatAt === 0
    const heartbeatIsRecent = now - state.sessionHeartbeatAt < heartbeatTtlMs
    const loginDateString = new Date(state.loginAt).toDateString()
    const timeSinceLoginMs = now - state.loginAt
    const loginIsRecent = loginDateString === new Date().toDateString() && timeSinceLoginMs < loginTtlMs
    const isSessionAlive = loginIsRecent && (heartbeatIsRecent || heartbeatNotYetEmitted)
    if (!isSessionAlive) {
      dispatch(logout())
    }
  }, [dispatch, store])
  useEffect(() => {
    verifyLocalStorage()
    checkSessionAlive()
  }, [auth, checkSessionAlive])
  useInterval(updateSessionHeartbeat, HEARTBEAT_INTERVAL_MS)
  useInterval(checkSessionAlive, EXPIRY_CHECK_INTERVAL_MS)
}

export default useSessionExpiryGuard
